import { Common, color } from '@stockbit/ui';
import Link from 'next/link';
import Image from 'global/Image';
import moment from 'moment';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as notesApi from 'lib/api/notes';
import alertMessage from 'global/AlertMessage';
import { PopoverCompany } from '../Popover';
import { ContentWrapper, HeaderContent, StreamOptionContainer, TextHeader } from '../Post/styled';
import TextContent from './TextContent';
import { ImagePlaceholder, NotesItemContainer, OptionItemContainer } from './styled';
import { QUERY_FETCH_NOTES } from './constants';
import ModalDeleteNote from './ModalDeleteNote';

const { Flex, Avatar, IconMoreOptions, Popover, Text } = Common;

type NotePostProps = {
  data: NoteItem;
  handleClickEdit: (id: number) => void;
}

const NotePost = ({ data, handleClickEdit }: NotePostProps) => {
  const queryClient = useQueryClient();

  const [isImageAvailable, setIsImageAvailable] = useState(true);

  const [showMenuPopover, setShowMenuPopover] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);

  const containerId = `notes-option-${data.id}`;

  const deleteNoteMutation = useMutation({
    mutationFn: async () => notesApi.deleteNotes(data.id),
    onSuccess: () => {
      setShowModalDelete(false);
      alertMessage({
        content: 'Note Deleted',
        alertType: 'snackbar-dark',
        messageType: 'success',
        hasCloseIcon: true,
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_FETCH_NOTES] });
    },
  });

  const onClickCopy = () => {
    setShowMenuPopover(false);
    navigator.clipboard.writeText(data.content.text);
    alertMessage({
      content: 'Text copied!',
      alertType: 'snackbar-dark',
      messageType: 'success',
      hasCloseIcon: true,
    });
  };

  const onClickDelete = () => {
    setShowModalDelete(true);
    setShowMenuPopover(false);
  };

  const handleDelete = () => {
    deleteNoteMutation.mutate();
  };

  const onClickEdit = () => {
    handleClickEdit(data.id);
    setShowMenuPopover(false);
  };

  return (
    <NotesItemContainer>
      <div id={containerId} />
      <Flex width="100%" className="header">
        <PopoverCompany symbol={data.company.symbol}>
          <span>
            <Link href={`/symbol/${data.company.symbol}?source=`} passHref>
              <a
                href="dummy"
                style={{ color: 'unset' }}
              >
                {isImageAvailable && (
                  <Avatar size="medium" padding="0">
                    <Image
                      alt="imageProfile"
                      src={data.company.icon_url}
                      onError={() => { setIsImageAvailable(false); }}
                      style={{ cursor: 'pointer' }}
                    />
                  </Avatar>
                )}

                {!isImageAvailable && (
                  <ImagePlaceholder>
                    <small>{!!data && data.company.symbol.toUpperCase().slice(0, 1)}</small>
                  </ImagePlaceholder>
                )}
              </a>
            </Link>
          </span>
        </PopoverCompany>
        <HeaderContent>
          <div>
            <Flex direction="column">
              <PopoverCompany symbol={data.company.symbol}>
                <Link href={`/symbol/${data.company.symbol}?source=`} passHref>
                  <TextHeader
                    size="16px"
                    family="bold"
                  >
                    {data.company.symbol}
                  </TextHeader>
                </Link>
              </PopoverCompany>
              <TextHeader size="12px" color={color.midgray}>
                {moment(data.created_at).format('D MMM YY, HH:mm')}
              </TextHeader>
            </Flex>
          </div>
          <Popover
            trigger={['contextMenu', 'click']}
            placement="bottomRight"
            getPopupContainer={() => document.getElementById(containerId)}
            overlayStyle={{ minWidth: '145px', paddingTop: 0 }}
            visible={showMenuPopover}
            onVisibleChange={(visible: boolean) => setShowMenuPopover(visible)}
            autoAdjustOverflow
            content={() => (
              <StreamOptionContainer>
                <OptionItemContainer onClick={onClickCopy}>
                  <Image width="16" height="15" src="icon/post-copy.svg" />
                  <Text ml="12px">Copy Text</Text>
                </OptionItemContainer>
                <OptionItemContainer onClick={onClickEdit}>
                  <Image width="16" height="15" src="icon/post-edit.svg" />
                  <Text ml="12px">Edit Note</Text>
                </OptionItemContainer>
                <OptionItemContainer onClick={onClickDelete}>
                  <Image width="16" height="15" src="icon/post-delete-red.svg" />
                  <Text ml="12px" color="#EE4A49">Delete Note</Text>
                </OptionItemContainer>
              </StreamOptionContainer>
            )}
          >
            <IconMoreOptions
              onClick={() => setShowMenuPopover(!showMenuPopover)}
            />
          </Popover>
        </HeaderContent>
      </Flex>
      <ContentWrapper>
        <TextContent
          content={data.content.masked_text}
          maskedHtml={data.content.masks}
        />
      </ContentWrapper>
      <ModalDeleteNote
        handleDelete={handleDelete}
        onCancel={() => setShowModalDelete(false)}
        visible={showModalDelete}
      />
    </NotesItemContainer>
  );
};

export default NotePost;
